import {isSubstring} from "@prosay/common"
import {DateFromNow} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import React, {useCallback, useMemo} from "react"
import {FlatList, StyleSheet, View} from "react-native"
import {List} from "react-native-paper"
import {Actions} from "react-native-router-flux"
import {FAQHeaderWithSearch, StatusSelector} from "../../components"
import {
    ConversationInfoFragment,
    ConversationStatus,
    useGetConversationsQuery,
} from "../../graphql"
import {useDate} from "../../hooks"
import {
    ConversationSearchContainer,
    ConversationStatusContainer,
} from "../../state"
import {getStatusText} from "../../util"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },

    conversationList: {
        flex: 1,
    },

    resolvedConversation: {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
})

export interface ConversationListItemProps extends ConversationInfoFragment {
    icon?: string
    onClick?: () => void
}
export const ConversationListItem: React.FC<ConversationListItemProps> = ({
    icon,
    onClick,
    ...conversation
}) => {
    const {
        case: {title},
        id,
        status,
        updatedAt,
    } = conversation

    const onPress = useCallback(() => {
        if (onClick) {
            onClick()
        }

        Actions.messages({
            id,
            subtitle: status,
            title,
        })
    }, [id, status, title, onClick])

    const date = useDate(updatedAt)

    return (
        <List.Item
            style={[
                status === "InProgress" ? styles.resolvedConversation : null,
            ]}
            onPress={onPress}
            title={title}
            description={getStatusText(conversation, true)}
            left={
                icon ? props => <List.Icon {...props} icon={icon} /> : undefined
            }
            right={() => <DateFromNow {...{date}} />}
        />
    )
}

// TODO: get proper icons
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getIcon = (_status: ConversationStatus) => {
    return ""
}

export interface ConversationsProps {
    type: "Client" | "Lawyer"
}
export const Conversations: React.FC<ConversationsProps> = () => {
    const [search, setSearch] = ConversationSearchContainer.useContainer()
    const [status] = ConversationStatusContainer.useContainer()
    const {data, fetchMore, loading, refetch} = useGetConversationsQuery({
        onCompleted: ({getConversations}) => {
            logger.debug`Got the following response from useConversations query: ${getConversations}`
        },
    })

    const onRefresh = useCallback(() => {
        logger.info`Conversation list refresh requested. Refreshing...`
        refetch()
    }, [refetch])

    const onEndReached = useCallback(async () => {
        logger.info`Reached the end of the conversations list. Fetching more...`

        try {
            await fetchMore({
                updateQuery: (prev, {fetchMoreResult}) => ({
                    ...prev,
                    getConversations: [
                        ...(prev.getConversations ?? []),
                        ...(fetchMoreResult?.getConversations ?? []),
                    ],
                }),
                variables: {
                    offset: data?.getConversations?.length,
                },
            })
        } catch (error) {
            logger.debug`Got the following error during fetchMore: ${error}`
        }
    }, [data, fetchMore])
    const resetSearchValue = useCallback(() => setSearch(""), [setSearch])
    const conversations = useMemo(() => {
        const conversations =
            (!search
                ? data?.getConversations
                : data?.getConversations?.filter(({case: {title}}) =>
                      isSubstring(title, search),
                  )) ?? []
        return conversations.filter(conversation =>
            status.includes(conversation.status),
        )
    }, [data, search, status])

    return (
        <View style={styles.container}>
            <FlatList
                style={styles.conversationList}
                onRefresh={onRefresh}
                refreshing={loading}
                data={conversations}
                keyExtractor={({id}) => id}
                renderItem={({item}) => (
                    <ConversationListItem
                        onClick={resetSearchValue}
                        icon={getIcon(item.status)}
                        {...item}
                    />
                )}
                onEndReachedThreshold={0.3}
                onEndReached={onEndReached}
            />
        </View>
    )
}

export interface ConversationsHeaderProps {
    type: "Client" | "Lawyer"
}
export const ConversationsHeader: React.FC<ConversationsHeaderProps> = ({
    type,
}) => {
    const [, setStatus] = ConversationStatusContainer.useContainer()

    return (
        <FAQHeaderWithSearch
            title={translate("ui.navigation.tabBar.conversations")}
            useStateHook={ConversationSearchContainer.useContainer}>
            <StatusSelector type={type} onStatusUpdate={setStatus} />
        </FAQHeaderWithSearch>
    )
}

export interface ClientConversationsProps
    extends Omit<ConversationsProps, "type"> {}
export const ClientConversations: React.FC<ClientConversationsProps> & {
    navigationOptions: {
        header: React.ReactNode
    }
} = ({...props}) => <Conversations type="Client" {...props} />
ClientConversations.navigationOptions = {
    header: <ConversationsHeader type="Client" />,
}

export interface LawyerConversationsProps
    extends Omit<ConversationsProps, "type"> {}
export const LawyerConversations: React.FC<LawyerConversationsProps> & {
    navigationOptions: {
        header: React.ReactNode
    }
} = ({...props}) => <Conversations type="Lawyer" {...props} />
LawyerConversations.navigationOptions = {
    header: <ConversationsHeader type="Lawyer" />,
}
